import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Slider, Stack, TextField, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { IndividualTextColorControl } from "../inspector/LandingInspectorControls";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { elementTextFallback, normalizeEditableColor, persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl } from "../inspector/VisualSettingControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import {
  buildElementColorVisualStyle,
  buildElementFontScaleVisualStyle,
  currentElementColor,
  currentElementFontScale,
  effectiveSectionPalette,
  getValueAtDescriptorPath,
  sectionVisualPath,
} from "../elementEditing";
import {
  HERO_TEXT_FONT_SCALE_MAX,
  HERO_TEXT_FONT_SCALE_MIN,
  HERO_TEXT_FONT_SCALE_STEP,
  isHeroTextFontScalePath,
} from "../heroTextStyle";

// WP-STANDARD | NAVBAR CONTEXTUAL GUIDE ANCHORS
// STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_RUNTIME_PENDING
// Structural wrappers only; text/color behavior remains owned by the existing editor.

function heroInheritedTextColor(form, descriptor, path, palette) {
  if (String(descriptor?.sectionType || "") !== "hero") return "";
  const visual = descriptor?.layout?.segment?.visualStyle || {};
  if (visual.useGlobalColors === false) {
    return palette.onSecondary || "#FFFFFF";
  }
  const candidate = String(path || "").toLowerCase();
  if (candidate.includes("eyebrow")) return "rgba(255, 255, 255, 0.78)";
  if (candidate.includes("subtitle")) return "rgba(255, 255, 255, 0.88)";
  if (candidate.includes("title")) {
    const hasPhrases = Array.isArray(form?.hero?.rotatingPhrases) &&
      form.hero.rotatingPhrases.some((value) => Boolean(String(value || "").trim()));
    return hasPhrases ? (form?.theme?.primaryColor || "#E44E31") : "#FFFFFF";
  }
  return palette.onSecondary || "#FFFFFF";
}

function StyleFieldControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    const fallback = String(field.defaultValue || "#000000");
    return (
      <VisualColorControl
        label={field.label}
        value={String(value ?? "")}
        fallback={fallback}
        disabled={disabled}
        helperText="Valor visual explícito de este elemento."
        allowExactCss
        validateExactValue={(candidate) => registryFieldError(field, candidate) || persistedCssColorError(candidate, field.maxLength || 100)}
        exactValueMaxLength={field.maxLength || 100}
        exactValueHelperText="Admite HEX, rgb/rgba, hsl/hsla y transparent."
        onChange={(next) => onCommit?.(field.path, next)}
        onReset={() => onCommit?.(field.path, fallback)}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <TextField fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  return <TextField fullWidth size="small" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ maxLength: field.maxLength || 300 }} helperText={/Shadow/i.test(field.path || "") ? "CSS exacto de sombra (ej. 0 12px 30px rgba(...))." : undefined} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
}

function textColorFallback(form, descriptor, palette) {
  const path = String(descriptor?.fieldPath || descriptor?.canvasPath || "");
  const heroFallback = heroInheritedTextColor(form, descriptor, path, palette);
  return heroFallback || elementTextFallback(path, palette, descriptor?.sectionType || descriptor?.sectionId || "");
}

export default function TextElementEditor({ descriptor, form, baselineForm, disabled, onCommit }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  const current = String(getValueAtDescriptorPath(form, path, "") ?? "");
  const [draft, setDraft] = React.useState(current);

  React.useEffect(() => setDraft(current), [current, path]);
  if (!field || !path) return null;

  const palette = effectiveSectionPalette(form, descriptor);
  const explicitColorPath = String(descriptor?.colorPath || field?.colorPath || "");
  const explicitColorField = explicitColorPath
    ? (descriptor?.definition?.properties || []).find((property) => property?.path === explicitColorPath)
    : null;
  const collectionColorKey = descriptor?.collection?.itemField?.colorKey || "";
  const explicitCollectionColorField = collectionColorKey
    ? (descriptor?.collection?.property?.item?.fields || []).find((candidate) => candidate?.key === collectionColorKey)
    : null;
  const fallback = normalizeEditableColor(
    explicitColorPath
      ? (explicitColorField?.defaultValue || explicitCollectionColorField?.defaultValue || getValueAtDescriptorPath(baselineForm, explicitColorPath, "") || "#000000")
      : textColorFallback(form, descriptor, palette)
  ) || "#000000";
  const colorPath = descriptor?.canvasPath || path;
  const ownColor = explicitColorPath
    ? String(getValueAtDescriptorPath(form, explicitColorPath, fallback) || fallback)
    : currentElementColor(form, descriptor, colorPath);
  const ownFontScale = currentElementFontScale(form, descriptor, colorPath);
  const sizeEligible = String(descriptor?.sectionType || "") === "hero" && isHeroTextFontScalePath(colorPath);
  const sliderScale = ownFontScale ?? 1;
  const visualPath = sectionVisualPath(descriptor);
  const styleFields = ["imageCarousel", "testimonials"].includes(String(descriptor?.sectionType || ""))
    ? (descriptor?.styleFields || [])
    : (descriptor?.styleFields || []).filter((candidate) => candidate?.path !== explicitColorPath);

  const commitColor = (nextColor) => {
    if (explicitColorPath) {
      onCommit?.(explicitColorPath, String(nextColor || "").trim() || fallback);
      return;
    }
    if (!visualPath) return;
    onCommit?.(visualPath, buildElementColorVisualStyle(form, descriptor, colorPath, nextColor));
  };
  const commitFontScale = (nextScale) => {
    if (!visualPath) return;
    onCommit?.(visualPath, buildElementFontScaleVisualStyle(form, descriptor, colorPath, nextScale));
  };

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
        <Chip size="small" label={explicitColorPath ? "Color explícito" : (ownColor ? "Color propio" : "Color heredado")} variant={explicitColorPath || ownColor ? "filled" : "outlined"} />
        {sizeEligible ? (
          <Chip
            size="small"
            label={ownFontScale === null ? "Tamaño heredado" : `Tamaño ${Math.round(ownFontScale * 100)}%`}
            variant={ownFontScale === null ? "outlined" : "filled"}
          />
        ) : null}
      </Stack>
      <Box data-wp-navbar-guide-target="text-content">
        <LandingCopyTextField
        fullWidth
        size="small"
        label={field.label || descriptor.label || "Texto"}
        field={field}
        path={path}
        form={form}
        baselineForm={baselineForm}
        value={draft}
        disabled={disabled}
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 4 : undefined}
        commitMode="change"
        onChange={(event) => setDraft(event.target.value)}
        onCommit={(nextValue) => {
          if (nextValue !== current) onCommit?.(path, nextValue);
        }}
        onKeyDown={(event) => {
          if (field.type !== "textarea" && event.key === "Enter" && !event.nativeEvent?.isComposing) {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        />
      </Box>
      <Box data-wp-navbar-guide-target="text-color">
        <IndividualTextColorControl
        fieldPath={explicitColorPath || colorPath}
        label={field.label || descriptor.label || "Texto"}
        value={ownColor}
        fallback={fallback}
        disabled={disabled}
        onChange={commitColor}
        onReset={() => commitColor(explicitColorPath ? fallback : "")}
        />
      </Box>
      {styleFields.length ? (
        <Accordion data-wp-navbar-guide-target="text-appearance" disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia avanzada</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((styleField) => <StyleFieldControl key={styleField.path} field={styleField} form={form} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
      {sizeEligible ? (
        <Box data-wp-navbar-guide-target="text-size" sx={{ p: 1, borderRadius: 2.25, bgcolor: "rgba(25,36,47,.035)", border: "1px solid rgba(25,36,47,.08)" }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.64)", fontWeight: 900 }}>
              Tamaño · {ownFontScale === null ? "Predeterminado (100%)" : `${Math.round(sliderScale * 100)}%`}
            </Typography>
            <Button
              size="small"
              disabled={disabled || ownFontScale === null}
              onClick={() => commitFontScale(1)}
              sx={{ minWidth: 0, px: 0.9, textTransform: "none", fontWeight: 850 }}
            >
              Heredar
            </Button>
          </Stack>
          <Slider
            size="small"
            min={HERO_TEXT_FONT_SCALE_MIN}
            max={HERO_TEXT_FONT_SCALE_MAX}
            step={HERO_TEXT_FONT_SCALE_STEP}
            value={sliderScale}
            disabled={disabled}
            marks={[
              { value: HERO_TEXT_FONT_SCALE_MIN, label: "70%" },
              { value: 1, label: "100%" },
              { value: HERO_TEXT_FONT_SCALE_MAX, label: "140%" },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(Number(value) * 100)}%`}
            aria-label={`Tamaño de ${field.label || descriptor.label || "texto"}`}
            onChange={(_, value) => commitFontScale(Array.isArray(value) ? value[0] : value)}
            sx={{ px: 0.25 }}
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.4, color: "rgba(25,36,47,.58)" }}>
            Escala el tamaño responsive de este texto sin fijarlo en píxeles.
          </Typography>
        </Box>
      ) : null}
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.6)" }}>
        {explicitColorPath ? "Restablecer vuelve al valor explícito predeterminado de este texto." : "Heredar elimina únicamente el ajuste propio y conserva los demás estilos del texto."}
      </Typography>
    </Stack>
  );
}
